import {useEffect, useRef, useState} from "react";
import {Alert, Box, Button, Dialog, DialogContent, Typography} from "@mui/material";
import QrCodeScannerRoundedIcon from "@mui/icons-material/QrCodeScannerRounded";
import {useKioskLang} from "../context/KioskSessionContext";

//自助點餐折扣碼掃描：前置鏡頭對準客人手機上的轉盤券 QR（POS 端的 CouponRedeem 用後置）
export default function CouponScanDialog({open, onClose, onDetect}) {
  const {tr} = useKioskLang();
  const videoRef = useRef(null);
  const detectedRef = useRef(false);
  const onDetectRef = useRef(onDetect);
  onDetectRef.current = onDetect;
  const [cameraError, setCameraError] = useState("");
  const [cameraNonce, setCameraNonce] = useState(0);

  useEffect(() => {
    if (!open) return;
    detectedRef.current = false;
    setCameraError("");
    let cancelled = false;
    let stream;
    let timer;
    const start = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({video: {facingMode: {ideal: "user"}}, audio: false});
        if (cancelled) return stream.getTracks().forEach((track) => track.stop());
        //相機被系統收回（iPad 休眠、權限被撤銷等）時提示重新啟用，而不是黑畫面卡住
        stream.getTracks().forEach((track) => {
          track.onended = () => {
            if (!cancelled) setCameraError(tr("cameraStopped"));
          };
        });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        if (globalThis.BarcodeDetector) {
          const detector = new globalThis.BarcodeDetector({formats: ["qr_code"]});
          timer = setInterval(async () => {
            if (!videoRef.current || detectedRef.current) return;
            try {
              const results = await detector.detect(videoRef.current);
              const code = results[0]?.rawValue;
              if (code) {
                detectedRef.current = true;
                onDetectRef.current(code);
              }
            } catch {}
          }, 350);
        } else {
          setCameraError(tr("scanNotSupported"));
        }
      } catch (error) {
        setCameraError(error?.name === "NotAllowedError" ? tr("scanCameraDenied") : tr("scanCameraUnavailable"));
      }
    };
    start();
    return () => {
      cancelled = true;
      clearInterval(timer);
      stream?.getTracks().forEach((track) => track.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cameraNonce]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogContent sx={{p: 2.5}}>
        <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 1.5}}>
          <QrCodeScannerRoundedIcon color="primary" sx={{fontSize: 30}}/>
          <Typography sx={{fontSize: 22, fontWeight: 950}}>{tr("scanCouponTitle")}</Typography>
        </Box>
        <Typography color="text.secondary" sx={{fontSize: 16, mb: 1.5}}>{tr("scanCouponHint")}</Typography>
        <Box sx={{position: "relative", aspectRatio: "4 / 3", overflow: "hidden", borderRadius: 1.5, bgcolor: "#1F1A17", display: "grid", placeItems: "center"}}>
          <Box component="video" ref={videoRef} playsInline muted sx={{width: "100%", height: "100%", objectFit: "cover", transform: "scaleX(-1)"}}/>
          <Box sx={{position: "absolute", width: "56%", aspectRatio: "1", border: "4px solid #FFF", borderRadius: 2, boxShadow: "0 0 0 999px rgba(0,0,0,.35)"}}/>
        </Box>
        {cameraError && <Alert severity="warning" sx={{mt: 1.5, alignItems: "center"}} action={<Button color="inherit" size="small" onClick={() => setCameraNonce((nonce) => nonce + 1)} sx={{fontWeight: 900, whiteSpace: "nowrap"}}>{tr("cameraRetry")}</Button>}>{cameraError}</Alert>}
        <Button fullWidth variant="outlined" onClick={onClose} sx={{mt: 2, minHeight: 52}}>{tr("cancel")}</Button>
      </DialogContent>
    </Dialog>
  );
}
